
import React, { useState, useEffect, useCallback } from 'react';
import { useAssemblyAIRecording } from '@/hooks/useAssemblyAIRecording';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Mic, MicOff, Square, Save, Loader2, CheckCircle, AlertCircle, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { SpeechLanguageSelector } from './SpeechLanguageSelector';

interface UnifiedSessionRecordingProps {
  bookingId: string;
  practitionerId: string;
  clientId: string;
  onTranscriptSaved?: (transcriptId: string) => void;
}

export const UnifiedSessionRecording: React.FC<UnifiedSessionRecordingProps> = ({
  bookingId,
  practitionerId,
  clientId,
  onTranscriptSaved
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [duration, setDuration] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [savedTranscriptId, setSavedTranscriptId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  // Map language selector to AssemblyAI language codes
  const getAssemblyAILanguage = (lang: string) => {
    if (lang.startsWith('zh')) return 'zh';
    if (lang.startsWith('en')) return 'en_us';
    return 'en_us'; // default to English (US)
  };

  const {
    isRecording,
    transcript,
    liveTranscript,
    cumulativeTranscript,
    error,
    isSupported,
    connectionStatus,
    sessionDuration,
    processedSegments,
    startRecording,
    stopRecording,
    resetTranscript,
    resetSession,
    retryConnection
  } = useAssemblyAIRecording({
    language: getAssemblyAILanguage(selectedLanguage),
    speakers: selectedLanguage.startsWith('zh') ? ['疗愈师', '客户'] : ['Therapist', 'Client']
  });

  // 录音时长计时器
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (isRecording) {
      interval = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  // 格式化时长为 MM:SS
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // 保存转录到数据库，包含改进的用户反馈
  const saveTranscript = useCallback(async () => {
    const finalTranscript = transcript || cumulativeTranscript;
    if (!finalTranscript?.trim()) {
      toast({
        title: "无法保存",
        description: "没有录音内容可以保存",
        variant: "destructive"
      });
      return;
    }

    setSaveStatus('saving');
    setIsSaving(true);
    
    try {
      console.log('💾 开始保存转录文本...');
      
      const { data, error } = await supabase
        .from('session_transcripts')
        .insert({
          booking_id: bookingId,
          practitioner_id: practitionerId,
          client_id: clientId,
          transcript_text: finalTranscript,
          recording_duration_seconds: duration,
          status: 'draft'
        })
        .select()
        .single();

      if (error) {
        console.error('保存转录文本错误:', error);
        throw error;
      }

      console.log('✅ 转录文本保存成功，ID:', data.id);
      setSavedTranscriptId(data.id);
      setSaveStatus('success');
      onTranscriptSaved?.(data.id);
      
      toast({
        title: "保存成功",
        description: `转录文本已成功保存 (ID: ${data.id})`,
        duration: 5000,
      });

      await processWithAI(data.id, finalTranscript);

    } catch (error) {
      console.error('保存转录文本失败:', error);
      setSaveStatus('error');
      toast({
        title: "保存失败",
        description: error instanceof Error ? error.message : "保存转录文本时发生错误",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [transcript, cumulativeTranscript, duration, bookingId, practitionerId, clientId, onTranscriptSaved, toast]);

  // AI 处理转录文本
  const processWithAI = async (transcriptId: string, transcriptText: string) => {
    setIsProcessingAI(true);
    try {
      console.log('🤖 开始 AI 分析转录文本...');
      
      const { data, error } = await supabase.functions.invoke('process-session-transcript', {
        body: {
          transcriptId,
          transcriptText
        }
      });

      if (error) {
        console.error('AI 处理错误:', error);
        throw error;
      }

      console.log('✅ AI 分析完成');
      toast({
        title: "AI 分析完成",
        description: "医疗记录已生成",
        duration: 3000,
      });

    } catch (error) {
      console.error('AI 处理失败:', error);
      toast({
        title: "AI 分析失败",
        description: "转录文本已保存，但 AI 分析失败",
        variant: "destructive"
      });
    } finally {
      setIsProcessingAI(false);
    }
  };

  // 处理开始录音
  const handleStartRecording = () => {
    setDuration(0);
    setSavedTranscriptId(null);
    setSaveStatus('idle');
    startRecording();
  };

  // 处理停止录音
  const handleStopRecording = () => {
    stopRecording();
  };

  // 处理重置
  const handleReset = () => {
    resetTranscript();
    setDuration(0);
    setSavedTranscriptId(null);
    setSaveStatus('idle');
  };

  // 处理完整会话重置
  const handleResetSession = () => {
    resetSession();
    setDuration(0);
    setSavedTranscriptId(null);
    setSaveStatus('idle');
  };

  if (!isSupported) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MicOff className="h-5 w-5" />
            录音功能不可用
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            此浏览器不支持录音功能，请使用现代浏览器。
          </p>
        </CardContent>
      </Card>
    );
  }

  const finalTranscript = transcript || cumulativeTranscript;

  return (
    <Card className={`w-full mx-auto ${isMobile ? 'max-w-full' : 'max-w-4xl'}`}>
      <CardHeader className={isMobile ? 'pb-3' : ''}>
        <CardTitle className={`flex items-center ${isMobile ? 'flex-col gap-3' : 'justify-between'}`}>
          <div className="flex items-center gap-2">
            <Mic className="h-5 w-5" />
            <span className={isMobile ? 'text-lg' : ''}>
              {selectedLanguage.startsWith('zh') ? '中文语音识别录音' : 'Speech Recognition Recording'}
            </span>
          </div>
          <div className={`flex items-center gap-2 ${isMobile ? 'flex-wrap justify-center' : ''}`}>
            <Badge variant={isRecording ? "destructive" : "secondary"} className={isMobile ? 'text-xs' : ''}>
              {isRecording ? (selectedLanguage.startsWith('zh') ? "录音中" : "Recording") : (selectedLanguage.startsWith('zh') ? "已停止" : "Stopped")}
            </Badge>
            <Badge variant="outline" className={isMobile ? 'text-xs font-mono' : ''}>
              {formatDuration(duration)}
            </Badge>
            {connectionStatus !== 'disconnected' && (
              <Badge variant={connectionStatus === 'connected' ? 'default' : 'secondary'} className={isMobile ? 'text-xs' : ''}>
                {connectionStatus === 'connecting' && (selectedLanguage.startsWith('zh') ? '连接中' : 'Connecting')}
                {connectionStatus === 'connected' && (selectedLanguage.startsWith('zh') ? '已连接' : 'Connected')}
                {connectionStatus === 'failed' && (selectedLanguage.startsWith('zh') ? '连接失败' : 'Connection Failed')}
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* 语言选择器 */}
        <div className={`${isMobile ? 'w-full' : 'w-64'}`}>
          <SpeechLanguageSelector
            selectedLanguage={selectedLanguage}
            onLanguageChange={setSelectedLanguage}
            disabled={isRecording}
          />
        </div>

        {/* 录音控制 - 优化移动端布局 */}
        <div className={`flex items-center gap-2 ${isMobile ? 'flex-col space-y-2' : 'flex-wrap'}`}>
          <div className={`flex items-center gap-2 ${isMobile ? 'w-full' : ''}`}>
            {!isRecording ? (
              <Button 
                onClick={handleStartRecording}
                className={`flex items-center gap-2 ${isMobile ? 'flex-1 min-h-[48px]' : ''}`}
                disabled={isSaving || isProcessingAI}
              >
                <Mic className="h-4 w-4" />
                {selectedLanguage.startsWith('zh') ? '开始录音' : 'Start Recording'}
              </Button>
            ) : (
              <Button 
                onClick={handleStopRecording}
                variant="destructive"
                className={`flex items-center gap-2 ${isMobile ? 'flex-1 min-h-[48px]' : ''}`}
              >
                <Square className="h-4 w-4" />
                {selectedLanguage.startsWith('zh') ? '停止录音' : 'Stop Recording'}
              </Button>
            )}
            
            <Button 
              onClick={saveTranscript}
              disabled={!finalTranscript?.trim() || isRecording || isSaving || isProcessingAI}
              className={`flex items-center gap-2 ${isMobile ? 'flex-1 min-h-[48px]' : ''}`}
              variant={saveStatus === 'success' ? 'default' : 'outline'}
            >
              {saveStatus === 'saving' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saveStatus === 'success' ? (
                <CheckCircle className="h-4 w-4" />
              ) : saveStatus === 'error' ? (
                <AlertCircle className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saveStatus === 'saving' ? (selectedLanguage.startsWith('zh') ? '保存中...' : 'Saving...') : 
               saveStatus === 'success' ? (selectedLanguage.startsWith('zh') ? '已保存' : 'Saved') :
               saveStatus === 'error' ? (selectedLanguage.startsWith('zh') ? '保存失败' : 'Save Failed') : 
               (selectedLanguage.startsWith('zh') ? '保存转录' : 'Save Transcript')}
            </Button>
          </div>
          
          <div className={`flex items-center gap-2 ${isMobile ? 'w-full' : ''}`}>
            <Button 
              onClick={handleReset}
              variant="outline"
              disabled={isRecording || isSaving || isProcessingAI}
              className={isMobile ? 'flex-1 min-h-[44px]' : ''}
            >
              {selectedLanguage.startsWith('zh') ? '重置转录' : 'Reset Transcript'}
            </Button>

            <Button 
              onClick={handleResetSession}
              variant="outline"
              disabled={isRecording || isSaving || isProcessingAI}
              className={`flex items-center gap-2 ${isMobile ? 'flex-1 min-h-[44px]' : ''}`}
            >
              <RotateCcw className="h-4 w-4" />
              {selectedLanguage.startsWith('zh') ? '重置会话' : 'Reset Session'}
            </Button>
          </div>
        </div>

        {/* 连接失败时的重试按钮 */}
        {connectionStatus === 'failed' && (
          <Button 
            onClick={retryConnection}
            variant="outline"
            className={`flex items-center gap-2 ${isMobile ? 'w-full min-h-[44px]' : ''}`}
            disabled={isRecording}
          >
            <RotateCcw className="h-4 w-4" />
            {selectedLanguage.startsWith('zh') ? '重试连接' : 'Retry Connection'}
          </Button>
        )}

        {/* 状态和错误显示 */}
        {error && (
          <div className={`p-3 bg-destructive/10 border border-destructive/20 rounded-lg ${isMobile ? 'text-sm' : ''}`}>
            <p className="text-destructive">
              {selectedLanguage.startsWith('zh') ? '错误' : 'Error'}: {error}
            </p>
          </div>
        )}

        {isProcessingAI && (
          <div className={`p-3 bg-blue-50 border border-blue-200 rounded-lg ${isMobile ? 'text-sm' : ''}`}>
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <p className="text-blue-700">
                {selectedLanguage.startsWith('zh') ? '正在进行 AI 分析...' : 'Processing with AI...'}
              </p>
            </div>
          </div>
        )}

        {saveStatus === 'success' && savedTranscriptId && (
          <div className={`p-3 bg-green-50 border border-green-200 rounded-lg ${isMobile ? 'text-sm' : ''}`}>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <p className="text-green-700">
                {selectedLanguage.startsWith('zh') 
                  ? `转录文本已成功保存 (ID: ${savedTranscriptId})`
                  : `Transcript saved successfully (ID: ${savedTranscriptId})`
                }
              </p>
            </div>
          </div>
        )}

        <Separator />

        {/* 实时转录显示 - 移动端优化 */}
        <div className="space-y-2">
          <div className={`flex items-center ${isMobile ? 'flex-col gap-2' : 'justify-between'}`}>
            <h3 className={`font-medium ${isMobile ? 'text-center' : ''}`}>
              {selectedLanguage.startsWith('zh') ? '实时转录' : 'Live Transcription'}
            </h3>
            <div className={`flex items-center gap-2 text-sm text-muted-foreground ${isMobile ? 'justify-center' : ''}`}>
              <span>
                {selectedLanguage.startsWith('zh') ? '已处理片段' : 'Processed segments'}: {processedSegments}
              </span>
              {sessionDuration > 0 && (
                <span>
                  {selectedLanguage.startsWith('zh') ? '会话时长' : 'Session duration'}: {Math.round(sessionDuration / 1000)}s
                </span>
              )}
            </div>
          </div>
          
          <ScrollArea className={`w-full border rounded-lg p-3 ${isMobile ? 'h-48' : 'h-64'}`}>
            <div className="space-y-3">
              {finalTranscript && (
                <div className="text-sm">
                  <strong className="text-green-600">
                    {selectedLanguage.startsWith('zh') ? '累积转录' : 'Cumulative Transcript'}:
                  </strong>
                  <div className="mt-1 p-2 bg-green-50 rounded border-l-4 border-green-500">
                    <pre className={`whitespace-pre-wrap text-foreground font-mono ${isMobile ? 'text-xs' : 'text-xs'}`}>
                      {finalTranscript}
                    </pre>
                  </div>
                </div>
              )}
              
              {liveTranscript && (
                <div className="text-sm">
                  <strong className="text-blue-600">
                    {selectedLanguage.startsWith('zh') ? '实时转录' : 'Live Transcript'}:
                  </strong>
                  <div className="mt-1 p-2 bg-blue-50 rounded border-l-4 border-blue-500">
                    <p className={`text-muted-foreground italic ${isMobile ? 'text-xs' : ''}`}>{liveTranscript}</p>
                  </div>
                </div>
              )}
              
              {!finalTranscript && !liveTranscript && (
                <p className={`text-muted-foreground text-center ${isMobile ? 'py-6 text-sm' : 'py-8'}`}>
                  {isRecording 
                    ? (selectedLanguage.startsWith('zh') ? "正在监听语音..." : "Listening for speech...")
                    : (selectedLanguage.startsWith('zh') ? "点击开始录音按钮开始语音识别" : "Click start recording to begin speech recognition")
                  }
                </p>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* 统计信息 - 移动端优化 */}
        {finalTranscript && (
          <div className={`flex items-center text-muted-foreground ${isMobile ? 'flex-col gap-1 text-xs' : 'gap-4 text-sm'}`}>
            <span>
              {selectedLanguage.startsWith('zh') ? '字符数' : 'Characters'}: {finalTranscript.length}
            </span>
            <span>
              {selectedLanguage.startsWith('zh') ? '引擎' : 'Engine'}: AssemblyAI {selectedLanguage.startsWith('zh') ? '中文' : 'English'}
            </span>
            <span>
              {selectedLanguage.startsWith('zh') ? '语言' : 'Language'}: {selectedLanguage.startsWith('zh') ? '中文 (zh)' : 'English (en_us)'}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
